import React from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'motion/react';
import {
  DEMO_COMPANY,
  DISCOVER_COUNTS,
  DISCOVER_CRITERIA,
  DISCOVER_TICKERS,
  type DiscoverTicker,
} from '../heroTheaterData';

interface DiscoverSceneProps {
  /** 0–1 progress within Discover chapter */
  progress: number;
  reducedMotion: boolean;
}

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

function TickerLogo({
  symbol,
  tone,
  size = 'md',
}: {
  symbol: string;
  tone: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizeClass =
    size === 'lg'
      ? 'h-10 w-10 text-sm'
      : size === 'sm'
        ? 'h-7 w-7 text-[10px]'
        : 'h-8 w-8 text-[11px] sm:h-9 sm:w-9 sm:text-xs';

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-xl font-bold text-white shadow-sm ring-1 ring-black/5 ${sizeClass}`}
      style={{
        background: `linear-gradient(145deg, ${tone}, ${tone}cc)`,
      }}
      aria-hidden
    >
      {symbol.slice(0, 2)}
    </div>
  );
}

function TickerRow({
  ticker,
  culled,
  highlighted,
  reducedMotion,
  compact,
}: {
  ticker: DiscoverTicker;
  culled: boolean;
  highlighted: boolean;
  reducedMotion: boolean;
  compact?: boolean;
}) {
  return (
    <motion.div
      layout={!reducedMotion}
      className={`flex items-center gap-3 rounded-2xl px-2.5 py-2 ring-1 sm:gap-3.5 sm:px-3 sm:py-2.5 ${
        highlighted
          ? 'bg-emerald-50 ring-emerald-200 shadow-sm shadow-emerald-700/10'
          : culled
            ? 'bg-transparent ring-transparent'
            : 'bg-white/90 ring-black/[0.06] shadow-sm shadow-black/[0.03]'
      }`}
      initial={false}
      animate={{
        opacity: culled ? 0 : 1,
        height: culled ? 0 : 'auto',
        marginBottom: culled ? 0 : 8,
        paddingTop: culled ? 0 : undefined,
        paddingBottom: culled ? 0 : undefined,
        scale: culled ? 0.96 : 1,
        filter: culled ? 'blur(2px)' : 'blur(0px)',
      }}
      transition={{
        duration: reducedMotion ? 0 : 0.22,
        layout: { duration: 0.2 },
      }}
      style={{ overflow: 'hidden' }}
    >
      <TickerLogo symbol={ticker.symbol} tone={ticker.logoTone} size={compact ? 'sm' : 'md'} />
      <div className="min-w-0 flex-1 text-left">
        <p className="truncate text-xs font-bold tracking-tight text-gray-900 sm:text-[13px]">
          {ticker.symbol}
          <span className="font-medium text-gray-500"> · {ticker.name}</span>
        </p>
        <p className="truncate text-[11px] font-medium text-gray-400">{ticker.sector}</p>
      </div>
      {highlighted && (
        <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
          Match
        </span>
      )}
    </motion.div>
  );
}

/**
 * Abstract universe refined by criteria lenses — shown as a ticker list with logos.
 * Not a screener UI.
 */
export default function DiscoverScene({ progress, reducedMotion }: DiscoverSceneProps) {
  // Beat map:
  // 0–0.12: full list
  // 0.12–0.28: lens 0
  // 0.28–0.44: lens 1
  // 0.44–0.60: lens 2
  // 0.60–0.78: settle on survivors
  // 0.78–1.0: focus company lifts

  const lensesActive = reducedMotion
    ? 3
    : progress < 0.12
      ? 0
      : progress < 0.28
        ? 1
        : progress < 0.44
          ? 2
          : 3;

  const minSurvive =
    lensesActive === 0 ? -1 : lensesActive === 1 ? 1 : lensesActive === 2 ? 2 : 3;

  const countIndex = Math.min(lensesActive, DISCOVER_COUNTS.length - 1);
  const count = DISCOVER_COUNTS[countIndex];

  const focusPhase = reducedMotion ? 1 : clamp01((progress - 0.78) / 0.22);
  const showFocus = focusPhase > 0.05;

  // Phone shows fewer rows; survivors always included
  const phoneLimit = 10;
  const mdLimit = 14;
  const tickers = DISCOVER_TICKERS;

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-background px-3 pb-4 pt-8 sm:px-4 sm:pt-9 md:px-5 md:pt-4">
      {/* Header */}
      <div className="mb-5 flex justify-center sm:mb-5">
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-700 sm:text-base">
          Screener
        </p>
      </div>

      {/* Criteria lenses — text-input style outline */}
      <div className="mb-3.5 min-h-0 rounded-xl bg-white/70 px-2.5 py-2 ring-1 ring-inset ring-black/[0.08] shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] sm:mb-4 sm:min-h-[4.25rem] sm:rounded-2xl sm:px-3 sm:py-2.5 md:min-h-[4.75rem]">
        <div className="flex flex-wrap justify-start gap-2 sm:gap-2.5">
          {DISCOVER_CRITERIA.map((c, i) => {
            const on = lensesActive > i;
            return (
              <motion.span
                key={c.id}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold leading-none sm:gap-1 sm:px-2.5 sm:py-1.5 sm:text-[11px] ${
                  on
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-700/25'
                    : 'bg-background-dark text-gray-400 ring-1 ring-black/[0.06]'
                }`}
                animate={
                  reducedMotion
                    ? undefined
                    : on
                      ? { opacity: 1, y: 0, scale: 1.04 }
                      : { opacity: 0.65, y: 0, scale: 1 }
                }
                initial={reducedMotion ? false : { opacity: 0, y: 6, scale: 0.94 }}
                transition={{ type: 'spring', stiffness: 420, damping: 22, delay: i * 0.04 }}
              >
                <span className="hidden sm:inline">{c.label}</span>
                <span className="sm:hidden">{c.shortLabel}</span>
                <span className={on ? 'font-semibold text-emerald-100' : 'text-gray-400'}>
                  {c.threshold}
                </span>
              </motion.span>
            );
          })}
        </div>
      </div>

      {/* Companies remaining — centered under filters */}
      <div className="mb-3.5 flex justify-center">
        <motion.div
          className="rounded-full bg-background-dark px-3 py-1 ring-1 ring-black/[0.06]"
          key={count}
          initial={reducedMotion ? false : { scale: 0.92, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <span className="text-xs font-bold tabular-nums text-gray-900 sm:text-[13px]">
            {count.toLocaleString()}
          </span>{' '}
          <span className="text-[11px] font-medium text-gray-500">left</span>
        </motion.div>
      </div>

      {/* Ticker list */}
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl bg-gradient-to-b from-background-dark/80 to-background-dark/40 p-2 ring-1 ring-black/[0.05] sm:p-2.5">
        <LayoutGroup>
          <div className="h-full overflow-hidden">
            {tickers.map((ticker, i) => {
              const culled = minSurvive >= 0 && ticker.surviveLevel < minSurvive;
              const isFocus =
                showFocus && ticker.symbol === DEMO_COMPANY.symbol && !culled;
              const densityClass =
                i >= mdLimit
                  ? 'hidden lg:block'
                  : i >= phoneLimit
                    ? 'hidden md:block'
                    : 'block';

              return (
                <div key={ticker.id} className={densityClass}>
                  <TickerRow
                    ticker={ticker}
                    culled={culled || isFocus}
                    highlighted={false}
                    reducedMotion={reducedMotion}
                    compact={i >= phoneLimit}
                  />
                </div>
              );
            })}
          </div>
        </LayoutGroup>

        {/* Soft fade at bottom of list */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-background-dark/95 to-transparent"
          aria-hidden
        />
      </div>

      {/* Focus lift card */}
      <AnimatePresence>
        {showFocus && (
          <motion.div
            className="absolute inset-x-3 bottom-5 rounded-xl bg-background p-3 shadow-lg shadow-emerald-900/10 ring-1 ring-emerald-200 sm:inset-x-4 sm:bottom-6 sm:p-3.5"
            initial={reducedMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex items-center gap-3">
              <TickerLogo
                symbol={DEMO_COMPANY.symbol}
                tone={DEMO_COMPANY.logoTone}
                size="lg"
              />
              <div className="min-w-0 text-left">
                <p className="truncate text-sm font-bold tracking-tight text-gray-900">
                  {DEMO_COMPANY.symbol} · {DEMO_COMPANY.name}
                </p>
                <p className="text-[11px] font-medium text-gray-500">
                  {DEMO_COMPANY.sector} · matches all criteria
                </p>
              </div>
              <span className="ml-auto shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                Match
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
